import type { Survey } from 'types';
import type { TimelinePoint } from './survey-timeline';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

export type SurveySummary = {
  type: Survey['type'];
  score: number | null;
  answered: number;
  impressions: number;
  responseRate: number | null;
  average: number | null;
  breakdown: Record<string, number> | null;
  sentiment: {
    positive: number;
    neutral: number;
    negative: number;
    unclassified: number;
    comments: number;
    enabled: boolean;
  };
};

export type SurveyAnswer = {
  id: number;
  questionId: number;
  score: number | null;
  comment: string | null;
  choice: string | null;
  choices: string[] | null;
  sentiment: 'positive' | 'neutral' | 'negative' | null;
};

/** One person's submit: every answer it carried, read together. */
export type SurveySubmission = {
  key: string;
  timestamp: number;
  device: string | null;
  experimentVariantMap: Record<string, string> | null;
  answers: SurveyAnswer[];
};

export type SurveyFeed = {
  submissions: SurveySubmission[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
};

export type QuestionResult = {
  questionId: number;
  kind: string;
  label: string;
  answered: number;
  counts: { value: string; count: number }[];
  scale?: 'NPS' | 'CSAT' | 'CES';
  headline?: number | null;
};

export type TimelineInterval = 'day' | 'week';

export type SurveyTimeline = { interval: TimelineInterval; points: TimelinePoint[] };

/**
 * How long results stay fresh. Without it every window focus and every switch
 * between the Overview and Results tabs refetched every panel; the repo's other
 * statistics hooks use a minute too. A save, start or end still refreshes them at
 * once, because every results key sits under the survey's own ['SURVEY', id],
 * which useSurvey invalidates.
 */
export const RESULTS_STALE_MS = 60_000;

/** A results query's key, under the survey's own so useSurvey's invalidation reaches it. */
export const resultsKey = (surveyId: number, ...parts: readonly unknown[]) => ['SURVEY', surveyId, ...parts];

/**
 * The summary and the per-question results: what the top of the Results tab
 * needs. The feed (useSurveyFeed) and the timeline (useSurveyTimeline) keep their
 * own state in their own components, so paging the feed or switching the
 * timeline's interval re-renders that panel alone, not every chart on the tab.
 */
export const useSurveyResults = (surveyId: number) => {
  const statsClient = useClient(config.STATISTICS_URL);

  const {
    data: summary,
    isLoading,
    isError,
  } = useQuery<SurveySummary>({
    enabled: !!surveyId,
    queryKey: resultsKey(surveyId, 'summary'),
    queryFn: () => statsClient.get<SurveySummary>(`/surveys/${surveyId}/summary`),
    staleTime: RESULTS_STALE_MS,
  });

  const { data: questions } = useQuery<QuestionResult[]>({
    enabled: !!surveyId,
    queryKey: resultsKey(surveyId, 'questions'),
    queryFn: () => statsClient.get<QuestionResult[]>(`/surveys/${surveyId}/questions`),
    staleTime: RESULTS_STALE_MS,
  });

  return { summary, isLoading, isError, questions };
};

export type SurveyResultsManager = ReturnType<typeof useSurveyResults>;
